import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import {
  FaArrowLeft,
  FaBook,
  FaPlus,
  FaMinus,
  FaSearch,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import colors from '../constants/colors';

const StaffSubjects = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [staffLoading, setStaffLoading] = useState(true);

  useEffect(() => {
    fetchStaff();
    fetchSubjects();
  }, []);

  const getAuthToken = () => localStorage.getItem('authToken');

  const fetchStaff = async () => {
    setStaffLoading(true);
    const token = getAuthToken();
    try {
      const response = await fetch(`${appConstants.API_ROOT_URL}get-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: staffId }),
      });
      const data = await response.json();
      if (response.ok) {
        setStaff(data.data);
      } else {
        setError('Failed to load staff details');
      }
    } catch (error) {
      console.error('Failed to fetch staff details:', error);
      setError('Failed to load staff details');
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchSubjects = async () => {
    setLoading(true);
    setError('');
    const token = getAuthToken();
    try {
      const response = await fetch(
        `${appConstants.API_ROOT_URL}get-staff-subjects`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: staffId }),
        },
      );
      const data = await response.json();
      if (response.ok) {
        setSubjects(data.data || []);
      } else {
        setError('Failed to load subjects');
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setError('Failed to load subjects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSubject = async (subjectId) => {
    setSaving(true);
    const token = getAuthToken();
    try {
      const response = await fetch(
        `${appConstants.API_ROOT_URL}add-subject-to-staff`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ staff_id: staffId, subject_id: subjectId }),
        },
      );
      if (response.ok) {
        setSubjects((prevSubjects) =>
          prevSubjects.map((subject) =>
            subject.id === subjectId ? { ...subject, assigned: true } : subject,
          ),
        );
      } else {
        alert('Failed to assign subject');
      }
    } catch (error) {
      console.error('Failed to assign subject:', error);
      alert('Failed to assign subject');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSubject = async (subjectId) => {
    setSaving(true);
    const token = getAuthToken();
    try {
      const response = await fetch(
        `${appConstants.API_ROOT_URL}remove-subject-from-staff`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ staff_id: staffId, subject_id: subjectId }),
        },
      );
      if (response.ok) {
        setSubjects((prevSubjects) =>
          prevSubjects.map((subject) =>
            subject.id === subjectId
              ? { ...subject, assigned: false }
              : subject,
          ),
        );
      } else {
        alert('Failed to remove subject');
      }
    } catch (error) {
      console.error('Failed to remove subject:', error);
      alert('Failed to remove subject');
    } finally {
      setSaving(false);
    }
  };

  // Filter subjects based on search term
  const filteredSubjects = subjects.filter((subject) =>
    subject.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Calculate statistics
  const assignedCount = subjects.filter((s) => s.assigned).length;
  const totalCount = subjects.length;

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              <FaBook />
              {staffLoading
                ? 'Loading...'
                : `${staff?.name || 'Staff'} Subjects`}
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>
          </div>

          {/* Back Button */}
          <Button
            onClick={() => navigate(-1)}
            variant="light"
            size="sm"
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Go Back"
          >
            <FaArrowLeft />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {/* ...removed stats cards for cleaner UI... */}

        {/* Content Section */}
        {loading || staffLoading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading subjects...
            </p>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaBook
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Subjects Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm
                ? 'No subjects match your search criteria.'
                : 'No subjects available for assignment.'}
            </p>
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'white',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '80px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Subject Name
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '120px',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '120px',
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubjects.map((subject, index) => (
                    <tr
                      key={subject.id}
                      style={{
                        transition: 'background 0.2s ease',
                        backgroundColor: subject.assigned
                          ? 'rgba(40, 167, 69, 0.05)'
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = subject.assigned
                          ? 'rgba(40, 167, 69, 0.05)'
                          : 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#2c3e50',
                          fontWeight: '500',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          }}
                        >
                          {subject.assigned ? (
                            <FaCheck
                              style={{ color: '#28a745', fontSize: '0.9rem' }}
                            />
                          ) : (
                            <FaTimes
                              style={{ color: '#6c757d', fontSize: '0.9rem' }}
                            />
                          )}
                          {subject.name}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Badge
                          bg={subject.assigned ? 'success' : 'secondary'}
                          style={{
                            fontSize: '0.8rem',
                            padding: '6px 12px',
                            borderRadius: '6px',
                          }}
                        >
                          {subject.assigned ? 'Assigned' : 'Available'}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {subject.assigned ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveSubject(subject.id)}
                            disabled={saving}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              margin: '0 auto',
                            }}
                            title="Remove Subject"
                          >
                            <FaMinus /> Remove
                          </Button>
                        ) : (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleAssignSubject(subject.id)}
                            disabled={saving}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              margin: '0 auto',
                            }}
                            title="Assign Subject"
                          >
                            <FaPlus /> Assign
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {filteredSubjects.length} subjects
                {searchTerm && ' (filtered)'}
              </span>
              <div
                style={{ display: 'flex', gap: '15px', alignItems: 'center' }}
              >
                <span
                  style={{
                    color: '#28a745',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                  }}
                >
                  <FaCheck className="me-1" />
                  {assignedCount} Assigned
                </span>
                <span
                  style={{
                    color: '#6c757d',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                  }}
                >
                  <FaTimes className="me-1" />
                  {totalCount - assignedCount} Available
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StaffSubjects;
